export const STAGE2_CALENDAR_PUZZLE_POINTS = 120;

export const DAY_START_MIN = 9 * 60;
export const DAY_END_MIN = 18 * 60;

export const TIMELINE_SLOTS = [
  "9:00 AM",
  "10:00 AM",
  "11:00 AM",
  "12:00 PM",
  "1:00 PM",
  "2:00 PM",
  "3:00 PM",
  "4:00 PM",
  "5:00 PM",
  "6:00 PM",
];

export const MEETING_SIDEBAR_ORDER = [
  "standup",
  "deep_work",
  "one_on_one",
  "marketing",
  "email_admin",
  "client_pres",
  "code_review",
];

function minToLabel(totalMin) {
  const h24 = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  const period = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}

export function formatMinutes(totalMin) {
  return minToLabel(totalMin);
}

export const INITIAL_SCHEDULE = [
  {
    id: "standup",
    kind: "meeting",
    label: "Team Standup",
    startMin: 9 * 60,
    duration: 60,
    initialDuration: 60,
    icon: "👥",
    color: "#8b5cf6",
    editable: true,
  },
  {
    id: "deep_work",
    kind: "meeting",
    label: "Project Deep Work",
    startMin: 10 * 60,
    duration: 60,
    initialDuration: 60,
    icon: "💻",
    color: "#3b82f6",
    editable: true,
  },
  {
    id: "one_on_one",
    kind: "meeting",
    label: "1:1 with Manager",
    startMin: 16 * 60 + 30,
    duration: 60,
    initialDuration: 60,
    icon: "🤝",
    color: "#22c55e",
    editable: true,
  },
  {
    id: "client_pres",
    kind: "meeting",
    label: "Client Presentation",
    startMin: 12 * 60,
    duration: 60,
    initialDuration: 60,
    icon: "🎯",
    color: "#14b8a6",
    editable: true,
  },
  {
    id: "marketing",
    kind: "meeting",
    label: "Marketing Review",
    startMin: 14 * 60,
    duration: 60,
    initialDuration: 60,
    icon: "📈",
    color: "#f59e0b",
    editable: true,
  },
  {
    id: "code_review",
    kind: "meeting",
    label: "Code Review",
    startMin: 15 * 60,
    duration: 60,
    initialDuration: 60,
    icon: "🔍",
    color: "#7c3aed",
    editable: true,
  },
  {
    id: "email_admin",
    kind: "meeting",
    label: "Email & Admin",
    startMin: 15 * 60 + 45,
    duration: 60,
    initialDuration: 60,
    icon: "✉️",
    color: "#ef4444",
    editable: true,
  },
];

export const ADDABLE_ACTIVITIES = [
  {
    id: "walk_talk",
    kind: "walk_talk",
    label: "Walk & Talk",
    defaultDuration: 30,
    minDuration: 15,
    maxDuration: 60,
    step: 15,
    icon: "🚶",
    color: "#22c55e",
  },
  {
    id: "water",
    kind: "water",
    label: "Water Break",
    defaultDuration: 10,
    minDuration: 10,
    maxDuration: 60,
    step: 5,
    icon: "💧",
    color: "#0ea5e9",
  },
  {
    id: "stretch",
    kind: "stretch",
    label: "Stretch Break",
    defaultDuration: 20,
    minDuration: 5,
    maxDuration: 60,
    step: 5,
    icon: "🧘",
    color: "#14b8a6",
  },
  {
    id: "focus",
    kind: "focus",
    label: "Focus Block",
    defaultDuration: 20,
    minDuration: 15,
    maxDuration: 60,
    step: 5,
    icon: "🎯",
    color: "#2563eb",
  },
];

function blocksOverlap(a, b) {
  const aEnd = a.startMin + a.duration;
  const bEnd = b.startMin + b.duration;
  return a.startMin < bEnd && b.startMin < aEnd;
}

function mergeOccupiedIntervals(schedule) {
  const intervals = schedule
    .map((b) => ({ start: b.startMin, end: b.startMin + b.duration }))
    .filter((i) => i.end > i.start)
    .sort((a, b) => a.start - b.start);

  const merged = [];
  for (const interval of intervals) {
    const last = merged[merged.length - 1];
    if (!last || interval.start >= last.end) {
      merged.push({ ...interval });
    } else {
      last.end = Math.max(last.end, interval.end);
    }
  }
  return merged;
}

export function blockOverlapsAny(schedule, startMin, duration, ignoreId = null) {
  const candidate = { startMin, duration };
  return schedule.some((block) => {
    if (ignoreId && block.id === ignoreId) return false;
    return blocksOverlap(candidate, block);
  });
}

export function findFirstGap(schedule, duration) {
  if (duration <= 0) return null;

  const merged = mergeOccupiedIntervals(schedule);
  let cursor = DAY_START_MIN;

  for (const occupied of merged) {
    if (occupied.start - cursor >= duration) {
      return cursor;
    }
    cursor = Math.max(cursor, occupied.end);
  }

  if (DAY_END_MIN - cursor >= duration) {
    return cursor;
  }
  return null;
}

export function syncScheduleLayout(schedule) {
  const sorted = [...schedule].sort((a, b) => {
    if (a.startMin != null && b.startMin != null) return a.startMin - b.startMin;
    if (a.startMin != null) return -1;
    if (b.startMin != null) return 1;
    return String(a.id).localeCompare(String(b.id));
  });

  const placed = [];
  const result = [];

  for (const block of sorted) {
    let startMin = block.startMin;
    const fitsAt =
      startMin != null &&
      startMin >= DAY_START_MIN &&
      startMin + block.duration <= DAY_END_MIN &&
      !blockOverlapsAny(placed, startMin, block.duration);

    if (!fitsAt) {
      startMin = findFirstGap(placed, block.duration);
    }
    if (startMin == null) continue;
    const next = { ...block, startMin };
    placed.push(next);
    result.push(next);
  }

  return result;
}

export function moveBlockToStart(schedule, blockId, startMin) {
  const block = schedule.find((b) => b.id === blockId);
  if (!block || startMin == null) return schedule;

  if (
    startMin < DAY_START_MIN ||
    startMin + block.duration > DAY_END_MIN ||
    blockOverlapsAny(
      schedule.filter((b) => b.id !== blockId),
      startMin,
      block.duration
    )
  ) {
    return null;
  }

  const updated = schedule.map((b) =>
    b.id === blockId ? { ...b, startMin } : b
  );
  return syncScheduleLayout(updated);
}

export function getGapStartMinFromSegment(segment) {
  const match = String(segment?.key ?? "").match(/^gap-(\d+)$/);
  return match ? Number(match[1]) : null;
}

/** @deprecated use syncScheduleLayout */
export function resolveFloatingBlockOverlaps(schedule) {
  return syncScheduleLayout(schedule);
}

export function computeScheduleScore(checks) {
  const keys = [
    "meetingsReduced",
    "hasWalkTalk",
    "hasWater",
    "hasStretch",
    "hasFocus",
    "noOverlaps",
    "fitsDay",
  ];
  const met = keys.filter((k) => checks[k]).length;
  return Math.round((met / keys.length) * 100);
}

export function validateCalendarSchedule(schedule) {
  const meetingsReduced = schedule.some(
    (b) =>
      b.kind === "meeting" &&
      b.initialDuration != null &&
      b.duration < b.initialDuration
  );
  const hasWalkTalk = schedule.some((b) => b.kind === "walk_talk");
  const hasWater = schedule.some((b) => b.kind === "water");
  const hasStretch = schedule.some((b) => b.kind === "stretch");
  const hasFocus = schedule.some((b) => b.kind === "focus");

  let noOverlaps = true;
  for (let i = 0; i < schedule.length; i++) {
    for (let j = i + 1; j < schedule.length; j++) {
      if (blocksOverlap(schedule[i], schedule[j])) {
        noOverlaps = false;
        break;
      }
    }
    if (!noOverlaps) break;
  }

  const fitsDay = schedule.every(
    (b) =>
      b.startMin >= DAY_START_MIN &&
      b.startMin + b.duration <= DAY_END_MIN &&
      b.duration > 0
  );

  const checks = {
    meetingsReduced,
    hasWalkTalk,
    hasWater,
    hasStretch,
    hasFocus,
    noOverlaps,
    fitsDay,
  };

  const valid =
    meetingsReduced &&
    hasWalkTalk &&
    hasWater &&
    hasStretch &&
    hasFocus &&
    noOverlaps &&
    fitsDay;

  const missing = [];
  if (!meetingsReduced) missing.push("Shorten at least one meeting");
  if (!hasWalkTalk) missing.push("Add a walk-and-talk");
  if (!hasWater) missing.push("Add a water break");
  if (!hasStretch) missing.push("Add a stretch break");
  if (!hasFocus) missing.push("Add a focus time block");
  if (!noOverlaps) missing.push("Resolve overlapping events");
  if (!fitsDay) missing.push("Keep all events within 9 AM–6 PM");

  return {
    valid,
    checks,
    missing,
    score: computeScheduleScore(checks),
  };
}

export function cloneInitialSchedule() {
  return INITIAL_SCHEDULE.map((b) => ({ ...b }));
}

export function sortMeetingsForSidebar(schedule) {
  const order = new Map(MEETING_SIDEBAR_ORDER.map((id, i) => [id, i]));
  return schedule
    .filter((b) => b.kind === "meeting")
    .sort((a, b) => (order.get(a.id) ?? 99) - (order.get(b.id) ?? 99));
}

export function getAddableActivityByKind(kind) {
  return ADDABLE_ACTIVITIES.find((a) => a.kind === kind) ?? null;
}

export function canEditBlockDuration(block) {
  return block?.kind === "meeting";
}

export function getDurationLimitsForBlock(block) {
  if (block.kind === "meeting") {
    return {
      min: 15,
      max: block.initialDuration ?? 60,
      step: 15,
    };
  }
  const config = getAddableActivityByKind(block.kind);
  if (config) {
    return {
      min: config.minDuration,
      max: block.initialDuration ?? config.maxDuration,
      step: config.step,
    };
  }
  return { min: 15, max: 60, step: 15 };
}

export function sortActivitiesForSidebar(schedule) {
  const meetings = sortMeetingsForSidebar(schedule);
  const breaks = schedule
    .filter((b) => b.kind !== "meeting")
    .sort((a, b) => a.startMin - b.startMin || String(a.id).localeCompare(String(b.id)));
  return [...meetings, ...breaks];
}

export function buildTimelineSegments(schedule) {
  const sorted = [...schedule].sort(
    (a, b) => a.startMin - b.startMin || String(a.id).localeCompare(String(b.id))
  );
  const segments = [];
  let cursor = DAY_START_MIN;

  for (const block of sorted) {
    const gapBefore = Math.max(0, block.startMin - cursor);
    if (gapBefore > 0) {
      segments.push({
        type: "gap",
        key: `gap-${cursor}`,
        duration: gapBefore,
      });
    }
    segments.push({
      type: "block",
      key: block.id,
      block,
      duration: block.duration,
    });
    cursor = Math.max(cursor, block.startMin + block.duration);
  }

  const tailGap = DAY_END_MIN - cursor;
  if (tailGap > 0) {
    segments.push({
      type: "gap",
      key: `gap-${cursor}`,
      duration: tailGap,
    });
  }

  return segments;
}
